import {useState} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { changePassword, getCurrentUser, updateUserConfig } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { User, Lock, Eye, EyeOff, ShieldCheck, Loader2 } from 'lucide-react';
import type { ChangePasswordInput } from '@/types/auth';


export default function Profile() {
    const [passwordData, setPasswordData] = useState<ChangePasswordInput & { confirmPassword: string }>({
        email: '',
        new_password: '',
        confirmPassword: '',
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const currentUser = getCurrentUser();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Mutation para alterar senha
    const changePasswordMutation = useMutation({
        mutationFn: changePassword,
        onSuccess: () => {
            toast({
                title: 'Senha alterada!',
                description: 'Sua senha foi alterada com sucesso.',
            });
            setPasswordData({ email: '', new_password: '', confirmPassword: '' });
            setShowPassword(false);
            setShowConfirmPassword(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao alterar senha',
                description: error.response?.data?.message || error.response?.data?.detail || 'Ocorreu um erro ao alterar a senha.',
            });
        },
    });

    // Mutation para atualizar configurações
    useMutation({
        mutationFn: updateUserConfig,
        onSuccess: () => {
            toast({
                title: 'Configurações atualizadas!',
                description: 'Suas configurações foram salvas com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['userConfig'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar configurações',
                description: error.response?.data?.message || error.response?.data?.detail || 'Ocorreu um erro ao atualizar as configurações.',
            });
        },
    });
    const handlePasswordSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validações
        if (!passwordData.email || !passwordData.new_password || !passwordData.confirmPassword) {
            toast({
                variant: 'destructive',
                title: 'Campos obrigatórios',
                description: 'Por favor, preencha todos os campos.',
            });
            return;
        }

        if (passwordData.email !== currentUser?.email) {
            toast({
                variant: 'destructive',
                title: 'Email incorreto',
                description: 'O email informado não corresponde ao seu email.',
            });
            return;
        }

        if (passwordData.new_password.length < 6) {
            toast({
                variant: 'destructive',
                title: 'Senha muito curta',
                description: 'A senha deve ter no mínimo 6 caracteres.',
            });
            return;
        }

        if (passwordData.new_password !== passwordData.confirmPassword) {
            toast({
                variant: 'destructive',
                title: 'Senhas não coincidem',
                description: 'A nova senha e a confirmação devem ser iguais.',
            });
            return;
        }

        changePasswordMutation.mutate({
            email: passwordData.email,
            new_password: passwordData.new_password,
        });
    };
    if (!currentUser) {
        return null;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Perfil</h1>
                <p className="mt-2 text-gray-600">Gerencie suas informações pessoais e configurações</p>
            </div>

            {/* User Info Card */}
            <div className="rounded-lg bg-white shadow border border-gray-100 p-6">
                <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 h-20 w-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                        <User className="h-10 w-10 text-white" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-3">
                            <h2 className="text-2xl font-semibold text-gray-900">{currentUser.name}</h2>
                            {currentUser.owner && (
                                <Badge variant="default" className="gap-1">
                                    <ShieldCheck className="h-3 w-3" />
                                    Owner
                                </Badge>
                            )}
                            {currentUser.is_active ? (
                                <Badge variant="success">Ativo</Badge>
                            ) : (
                                <Badge variant="destructive">Inativo</Badge>
                            )}
                        </div>
                        <p className="text-gray-600 mt-1">{currentUser.email}</p>
                        <p className="text-sm text-gray-500 mt-2">
                            Último login:{' '}
                            {currentUser.last_login_at
                                ? new Date(currentUser.last_login_at).toLocaleString('pt-BR')
                                : 'Nunca'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs for Password and Settings */}
            <Tabs defaultValue="password" className="space-y-6">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                    <TabsTrigger value="password" className="gap-2">
                        <Lock className="h-4 w-4" />
                        Senha
                    </TabsTrigger>
                </TabsList>

                {/* Password Tab */}
                <TabsContent value="password">
                    <div className="rounded-lg bg-white shadow border border-gray-100 p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Lock className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Alterar Senha</h3>
                                <p className="text-sm text-gray-600">Atualize sua senha de acesso</p>
                            </div>
                        </div>

                        <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="seu@email.com"
                                    value={passwordData.email}
                                    onChange={(e) => setPasswordData({ ...passwordData, email: e.target.value })}
                                    disabled={changePasswordMutation.isPending}
                                />
                                <p className="text-xs text-gray-500">Confirme seu email para segurança</p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="new-password">Nova Senha *</Label>
                                <div className="relative">
                                    <Input
                                        id="new-password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={passwordData.new_password}
                                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                                        disabled={changePasswordMutation.isPending}
                                        className="pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        disabled={changePasswordMutation.isPending}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500">Mínimo 6 caracteres</p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirm-password">Confirmar Nova Senha *</Label>
                                <div className="relative">
                                    <Input
                                        id="confirm-password"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={passwordData.confirmPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                        disabled={changePasswordMutation.isPending}
                                        className="pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        disabled={changePasswordMutation.isPending}
                                    >
                                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            <div className="pt-4">
                                <Button type="submit" disabled={changePasswordMutation.isPending}>
                                    {changePasswordMutation.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Alterando...
                                        </>
                                    ) : (
                                        'Alterar Senha'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </TabsContent>

            </Tabs>
        </div>
    );
}